import React, {useCallback, useRef} from 'react';
import {useDropzone} from 'react-dropzone';
import PropTypes from 'prop-types';
import css from './FileUpload.module.scss';
import ImageAdd from "../../Icons/ImageAdd.jsx";
import Trash01 from "../../Icons/Trash01.jsx";
import Button from "../../Button/Button.jsx";
import EyeOpen from "../../Icons/EyeOpen.jsx";
import FlexContainer from "../../FlexContainer/FlexContainer.jsx";

const FileUpload = ({
  onFilesChange,
  files = [],
  label,
  required = false,
  maxFiles = 10,
  error,
  onFileDelete,
  fileClassName,
  accept,
}) => {
  const fileInputRef = useRef(null);

  const handleFiles = useCallback((files) => {
    const newFiles = Array.from(files)
      .filter(file => Object.hasOwn(accept, file.type))
      .map(file => {
        let previewUrl = URL.createObjectURL(file);

        return {
          file: file,
          previewUrl: previewUrl,
        };
      });

    const updatedFiles = maxFiles === 1
      ? newFiles.slice(0, 1)
      : [...files, ...newFiles].slice(0, maxFiles);

    onFilesChange?.(updatedFiles);
  }, [onFilesChange, maxFiles]);

  const onDrop = useCallback((acceptedFiles) => {
    handleFiles(acceptedFiles);
  }, [handleFiles]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback((e) => {
    handleFiles(e.target.files);
  }, [handleFiles]);

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept,
    noClick: true
  });

  const deleteFile = async (index) => {
    try {
      const newFiles = [...files];
      if (newFiles[index] && newFiles[index].id) {
        await onFileDelete(newFiles[index].id)
      }
      newFiles.splice(index, 1);
      onFilesChange?.(newFiles);
    } catch (error) {
      console.log(error);
    }
  };

  const openFile = (fileUrl) => {
    window.open(fileUrl)
  }

  const supportedFileTypes = [...new Set(Object.values(accept).flat())].join(', ');

  return (
    <div className={css.fileUpload}>
      {label &&
        <label className={css.label}>
          {label}
          {required && <span className={css.required}>*</span>}
        </label>
      }
      <div className={css.uploaderContainer}>
        {files.map((file, index) => {
          return (
            <div key={index} className={`${css.previewItem} ${fileClassName}`}>
              <Button variant="ghost" size="small" onClick={() => openFile(file.previewUrl)}>
                <EyeOpen/> View
              </Button>
              <button
                type="button"
                className={css.deleteButton}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteFile(index);
                }}
              >
                <Trash01 color="var(--danger)" width={14}/>
              </button>
              {file.file && <FlexContainer>{file.file.name}</FlexContainer>}
            </div>
          )
        })}
        <div
          {...getRootProps()}
          className={`${css.dropzone} ${fileClassName} ${isDragActive ? css.active : ''} ${error ? css.error : ''}`}
          onClick={handleClick}
          data-error={!!error}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple={maxFiles > 1}
            accept={accept}
            onChange={handleFileInputChange}
            style={{display: 'none'}}
            required={required && files.length === 0}
          />
          {isDragActive ?
            <p>Drop files here...</p>
            :
            <div className={css.content}>
              <ImageAdd width={20} color="var(--primary-color)"/>
              <div className={css.uploadText}>
                <span className={css.clickText}>Click to upload</span> or drag and drop
                <div className={css.supportText}>Supported files: {supportedFileTypes}</div>
              </div>
            </div>
          }
        </div>
      </div>
      {error && <span className={css.errorMessage}>{error}</span>}
    </div>
  );
};

FileUpload.propTypes = {
  onFilesChange: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.string
  ),
  label: PropTypes.string,
  required: PropTypes.bool,
  maxFiles: PropTypes.number,
  error: PropTypes.string,
  accept: PropTypes.object.isRequired,
};

export default FileUpload;
